import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

import {normalizeHex} from '@amblique/king-living/app/utils/normalize-hex';

const helpers = createMultiStyleConfigHelpers(['container', 'primaryRegion', 'secondaryRegion']);

/**
 * SplitPanelDoubleImageBlock styles (Double image carousel, CF-07)
 *
 * Figma Right: Desktop 3132-2147 (row, gap 110px, padding 90 0 0 80), Mobile 3132-6345 (column, gap 48px, padding 0 20px 80px 0).
 * Figma Left: Desktop 3132-3420 (top 90px, gutter 110px, right 80px; large left 810×810, r-col 440 at 920), Mobile 3132-6338 (column, padding 0 0 80px 20px).
 * Desktop: secondary r-col gap 52px; small image 440×440; title 24/Body Med, body 18/Body Reg, gap 16px. Mobile: secondary row gap 20px; small image 158×158; title 18px, body 16px, gap 8px.
 * CF-07: backgroundColour and textColour (hex) from Contentful when set.
 */
export const splitPanelDoubleImageBlockStyles = helpers.defineMultiStyleConfig({
  baseStyle: ({component}) => {
    const bgHex = normalizeHex(component?.backgroundColour);
    const copyColor = normalizeHex(component?.textColour) ?? 'brandColors.greyscale80';

    return {
      container: {
        width: '100%',
        maxWidth: {base: '100%', md: 'container.xxl'},
        marginLeft: {base: 0, md: 'auto'},
        marginRight: {base: 0, md: 'auto'},
        gap: {base: 12, md: 27.5},
        paddingTop: {base: 0, md: 22.5},
        paddingBottom: {base: 20, md: 20},
        ...(bgHex && {backgroundColor: bgHex}),
        sx: {
          '.component--image img.chakra-image': {
            display: 'block',
          },
        },
      },
      primaryRegion: {
        flex: {base: '1', md: '1 1 auto'},
        minWidth: {base: 0, md: 0},
        sx: {
          '.component--shop-the-look': {
            width: '100%',
            minWidth: 0,
          },
        },
      },
      secondaryRegion: {
        flex: {base: '1', md: '0 0 var(--chakra-sizes-doubleImageLarge, 440px)'},
        width: {base: 'auto', md: 'doubleImageLarge'},
        maxWidth: {base: 'none', md: '100%'},
        display: 'flex',
        flexDirection: {base: 'row', md: 'column'},
        gap: {base: 5, md: 13},
        alignItems: {base: 'flex-start', md: 'stretch'},
        sx: {
          '.component--image': {
            'flex': {base: '0 0 auto', md: '0 0 auto'},
            'width': {base: 'doubleImageSmall', md: '100%'},
            'height': {base: 'doubleImageSmall', md: 'doubleImageLarge'},
            'maxWidth': {base: 'doubleImageSmall', md: '100%'},
            'minWidth': 0,
            'overflow': 'hidden',
            '& img': {
              objectFit: 'cover',
              width: '100%',
              height: '100%',
            },
          },
          '.component--video': {
            flex: {base: '0 0 auto', md: '0 0 auto'},
            width: {base: 'doubleImageSmall', md: '100%'},
            height: {base: 'doubleImageSmall', md: 'doubleImageLarge'},
            maxWidth: {base: 'doubleImageSmall', md: '100%'},
            minWidth: 0,
          },
          '.component--shop-the-look': {
            flex: {base: '0 0 auto', md: '0 0 auto'},
            width: {base: 'doubleImageSmall', md: '100%'},
            maxWidth: {base: 'doubleImageSmall', md: '100%'},
            minWidth: 0,
            alignSelf: {base: 'flex-start', md: 'stretch'},
          },
          '.component--rich-text-block': {
            flex: {base: '1', md: '1'},
            minWidth: 0,
          },
          '.component--rich-text-block h1, .component--rich-text-block h2, .component--rich-text-block h3, .component--rich-text-block h4, .component--rich-text-block h5, .component--rich-text-block h6': {
            fontSize: {base: 'lg', md: '2xl'},
            fontWeight: '500',
            lineHeight: {base: '160%', md: '140%'},
            color: copyColor,
          },
          '.component--rich-text-block .chakra-text': {
            fontSize: {base: 'md', md: 'lg'},
            fontWeight: '400',
            lineHeight: '160%',
            color: copyColor,
            marginTop: {base: 2, md: 4},
          },
        },
      },
    };
  },
  variants: {
    // CF-07 variant left: Figma 3132-3420 – desktop top 90px, gutter 110px (baseStyle), right 80px
    left: () => ({
      container: {
        flexDirection: {base: 'column', md: 'row'},
        paddingLeft: {base: 0, md: 0},
        paddingRight: {base: 0, md: 20},
      },
      primaryRegion: {
        marginLeft: {base: 5, md: 0},
        marginRight: {base: 0, md: 0},
      },
      secondaryRegion: {
        paddingLeft: {base: 5, md: 0},
        paddingRight: {base: 5, md: 0},
      },
    }),
    // CF-07 variant right: same layout as left but mirrored – Figma 3132-2147. Secondary (image+text) left, Primary (large image) right; desktop left 80px padding
    right: () => ({
      container: {
        flexDirection: {base: 'column', md: 'row-reverse'},
        paddingLeft: {base: 0, md: 20},
        paddingRight: {base: 0, md: 0},
      },
      primaryRegion: {
        marginLeft: {base: 0, md: 0},
        marginRight: {base: 5, md: 0},
      },
      secondaryRegion: {
        paddingLeft: {base: 5, md: 0},
        paddingRight: {base: 5, md: 0},
        sx: {
          '.component--rich-text-block': {order: {base: 1, md: 'unset'}},
          '.component--image': {order: {base: 2, md: 'unset'}},
          '.component--video': {order: {base: 2, md: 'unset'}},
          '.component--shop-the-look': {order: {base: 2, md: 'unset'}},
        },
      },
    }),
  },
  defaultProps: {
    variant: 'left',
  },
});
